// @intl project:provider-profile

import { IntlShape } from 'react-intl'

import { fromJS, List as ImmutableList, Iterable } from 'immutable'
import { intersection, trim } from 'lodash-es'

import {
  PREFERRED_APPOINTMENT_MODALITIES,
  TREATMENT_MODE,
  TREATMENT_MODE_TO_PREFERRED_APPOINTMENT_MODALITY,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/appointmentModalityConstants'
import {
  PROGRAM_TAXONOMY,
  TRIAGE_TREATMENT_OPTIONS_TO_PROVIDER_TYPES_FOR_DISPLAY,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/customerProgram'
import { TREATMENT_OPTIONS_TYPE } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/treatmentOptions'
import { ImmutableTypedMap } from '@lyrahealth-inc/shared-app-logic/src/features/common/types/Immutable'
import { Programs } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programConfigs'
import { PROVIDER_CREDENTIALS } from '@lyrahealth-inc/shared-app-logic/src/features/providers/constants'
import {
  getPreferredContactInfo,
  getProviderSupportsLiveMessaging,
} from '@lyrahealth-inc/shared-app-logic/src/features/providers/utils'
import { Appointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import { LocationObject } from '@lyrahealth-inc/shared-app-logic/src/models/locationFinder/LocationFinder'
import {
  Address,
  Provider,
  PROVIDER_TYPES,
  ProviderInfo,
  ProviderProgramTaxonomy,
} from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'
import { MEETING_FORMATS } from '@lyrahealth-inc/shared-app-logic/src/models/session/Session'
import { Customer } from '@lyrahealth-inc/shared-app-logic/src/models/user/User'

/**
 * Return the appropriate contact information for a given provider, based on their preferred contact methods.
 * We have no guarantee on how consistent the provider data will be, so the logic is somewhat complex.
 * @param $$provider
 * @returns contact information
 */
export const getContactInformation = ($$provider: ImmutableTypedMap<ProviderInfo>) => {
  const providerInfo = $$provider.toObject() as unknown as ProviderInfo
  return getPreferredContactInfo(providerInfo)
}

/**
 * Returns whether the given provider is of a type specified in providerTypes.
 * e.g. isProviderAnyTypeOf({$$provider, [PROVIDER_TYPES.COACH, PROVIDER_TYPES.THERAPIST]}) will return whether the
 * provider is either a coach or a therapist
 * @param $$provider - An object representing a provider
 * @param providerTypes - An object representing a provider type (from the constant PROVIDER_TYPES), or an iterable
 *  object containing one or more provider types.
 * @returns {*|boolean}
 */
export const isProviderAnyTypeOf = ({
  $$provider,
  providerTypes = [],
}: {
  $$provider: ImmutableTypedMap<ProviderInfo>
  providerTypes: Array<{ lyraType: string }> | { lyraType: string }
}) => {
  const isIterable = Array.isArray(providerTypes) || Iterable.isIterable(providerTypes)
  const providerTypesIterable = isIterable ? providerTypes : ImmutableList([providerTypes])
  return (providerTypesIterable as Array<{ lyraType: string }>).some((providerType) => {
    return (
      $$provider &&
      $$provider.get('lyra_type', PROVIDER_TYPES.PROVIDER.lyraType).toLowerCase() === providerType?.lyraType
    )
  })
}

/**
 * @deprecated, please make your object mutable and use `shared-app-logic/../providers/utils/isProviderBlendedCare`
 * going forward
 */
export const isProviderBlendedCareType = ({ $$provider }: { $$provider: ImmutableTypedMap<ProviderInfo> }) => {
  return $$provider
    .getIn(['program_taxonomy'], ImmutableList())
    .some(
      (taxonomy: ImmutableTypedMap<ProviderProgramTaxonomy>) =>
        taxonomy.get('partner') === PROGRAM_TAXONOMY.partner.blendedCare,
    )
}

export const providerHasAvailability = ($$provider: ImmutableTypedMap<ProviderInfo>) => {
  return $$provider.getIn(['availabilityCS', 'provider_availability', 0, 'availability'], ImmutableList()).size > 0
}

export const getTypeConstantForProvider = ($$provider: ImmutableTypedMap<ProviderInfo>) => {
  return Object.values(PROVIDER_TYPES).find((providerTypeObject) => {
    return providerTypeObject.lyraType === $$provider.get('lyraType', PROVIDER_TYPES.PROVIDER.lyraType).toLowerCase()
  })
}

export const getIsProviderBCTFellow = (provider: ProviderInfo) => {
  return provider.program_taxonomy
    ? provider.program_taxonomy.some((pt) => pt?.offering === PROGRAM_TAXONOMY.offering.fellowship)
    : false
}

// Return a list of customer programs associated with the provider modalities,
// e.g. passing a modalities list of ['stressManagement', 'singleSessionCoaching'] would return ['stressManagement', 'guidedSelfCareEnabled']
export const getProviderProgramsFromProviderModalities = (modalities: string[]) => {
  return Programs.getProgramConfigsMatching(
    (programConfig) => intersection(programConfig.associatedProviderModalities, modalities).length > 0,
  ).map((matchedProgram) => matchedProgram.customerPropertyId)
}

export const formatAddressForDisplay = ($$address: ImmutableTypedMap<Address>) => {
  const addressLine1 = `${$$address.get('street1')}`.concat(
    trim($$address.get('street2', '')) ? `, ${$$address.get('street2')}` : '',
  )
  const addressLine2 = `${$$address.get('city')}, ${$$address.get('state')} ${$$address.get(
    'zipcode',
    $$address.get('zip', ''),
  )}`
  return { addressLine1, addressLine2 }
}

export const getProvidersMatchingGivenAppointmentModalities = ({
  providers,
  modalities,
}: {
  providers: ImmutableList<ImmutableTypedMap<ProviderInfo>>
  modalities: ImmutableList<string>
}) => {
  if (modalities.includes(PREFERRED_APPOINTMENT_MODALITIES.NO_PREFERENCE)) {
    return providers
  } else {
    return providers.filter((provider) => {
      // Convert from the provider's `supportedVisitTypes` attribute (aka their supported modalities) to the preferred
      // modality enum values, then return all providers who have at least one modality that supports the client's choices
      if (!provider) return false
      return (
        intersection(
          TREATMENT_MODE_TO_PREFERRED_APPOINTMENT_MODALITY[provider.get('supportedVisitTypes', TREATMENT_MODE.NONE)],
          modalities.toJS ? modalities.toJS() : modalities,
        ).length > 0
      )
    })
  }
}

export const getProviderTitle = ({ formatMessage }: IntlShape, $$provider: ImmutableTypedMap<ProviderInfo>): string => {
  const providerType = getTypeConstantForProvider($$provider)
  return formatMessage(providerType?.displayTitle ?? PROVIDER_TYPES.PROVIDER.displayTitle)
}

export const getProviderCredential = ({ formatMessage }: IntlShape, credential: string): string => {
  if (PROVIDER_CREDENTIALS[credential]) {
    return formatMessage(PROVIDER_CREDENTIALS[credential])
  } else {
    return credential
  }
}

export const getProvidersModalitySupport = (
  $$provider: ImmutableTypedMap<ProviderInfo>,
  $$customer: ImmutableTypedMap<Customer>,
  selectedTreatmentOption: TREATMENT_OPTIONS_TYPE,
) => {
  const modalities: Array<MEETING_FORMATS> =
    TREATMENT_MODE_TO_PREFERRED_APPOINTMENT_MODALITY[$$provider.get('supportedVisitTypes', TREATMENT_MODE.NONE)]
  return {
    isOnsite: $$provider.get('onsite', false),
    supportsVideo: modalities.includes(MEETING_FORMATS.VIDEO),
    supportsInPerson: modalities.includes(MEETING_FORMATS.IN_PERSON),
    supportsLiveMessaging:
      $$customer.getIn(['customerPropertiesMap', 'liveMessagingEnabled']) === 'true' &&
      getProviderSupportsLiveMessaging({ provider: $$provider.toJS(), selectedTreatmentOption }),
  }
}

export const getProviderTypeForSelectedTreatmentOption = (selectedTreatmentOption: string) => {
  return (
    TRIAGE_TREATMENT_OPTIONS_TO_PROVIDER_TYPES_FOR_DISPLAY[
      selectedTreatmentOption as keyof typeof TRIAGE_TREATMENT_OPTIONS_TO_PROVIDER_TYPES_FOR_DISPLAY
    ] ?? PROVIDER_TYPES.PROVIDER
  )
}

export const getTimeZoneLookupData = (locationData: LocationObject, providerId: string) => {
  const timeZoneLookupData = {
    latLong: '',
    locationState: locationData.geo?.state,
    providerIds: [providerId],
    location: '',
  }
  if (locationData.geo?.lat) {
    timeZoneLookupData.latLong = locationData.geo.lat + ', ' + locationData.geo.lon
  } else {
    timeZoneLookupData.location = locationData.geo?.formattedAddress ?? ''
  }

  return timeZoneLookupData
}

export const appointmentIsUpcomingOnPlatform = (appointment: Appointment, meetingFormat: string) => {
  return (
    appointment.meetingFormat === meetingFormat &&
    ['newAppt', 'rescheduled'].includes(appointment.appointmentStatus) &&
    isProviderBlendedCareType({ $$provider: fromJS(appointment.provider) })
  )
}

/**
 * Helper to get the episode current session count with a provider
 * @param {*} providerId string
 * @param {*} episodeId string
 * @param {*} providers Providers
 * @returns number
 */
export const getProviderSessionCounts = (providerId: string, episodeId: string | null, providers: Provider[]) => {
  const provider = providers.find((provider) => provider.provider.lyra_id === providerId)
  return (episodeId && provider?.provider?.episodeSessionCounts?.[episodeId]) || 0
}
